import { useState, useRef, useEffect } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { DownloadSettings, getDownloadSettings } from '@/lib/cms';

/* ── Download Button ── */
function DownloadButton({ platform, href, label }: { platform: 'ios' | 'android'; href: string; label: string }) {
  const isIOS = platform === 'ios';
  const configured = Boolean(href);
  return (
    <a
      href={configured ? href : '#'}
      target={configured ? '_blank' : undefined}
      rel={configured ? 'noreferrer' : undefined}
      download={configured && !isIOS ? true : undefined}
      onClick={(e) => {
        if (!configured) e.preventDefault();
      }}
      aria-disabled={!configured}
      className={`flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-300 border cursor-pointer ${
        isIOS
          ? 'bg-accent-500 text-background-950 border-accent-400 hover:bg-accent-400 hover:shadow-[0_0_25px_rgba(200,160,80,0.3)]'
          : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25 hover:border-emerald-400/50 hover:shadow-[0_0_20px_rgba(72,209,176,0.15)]'
      } ${configured ? '' : 'opacity-60 cursor-not-allowed'}`}
    >
      <div className="w-7 h-7 flex items-center justify-center">
        <i className={`${isIOS ? 'ri-apple-fill' : 'ri-android-fill'} text-xl`}></i>
      </div>
      <div className="text-left leading-tight">
        <span className="block text-[9px] font-normal opacity-70">{isIOS ? 'Download on the' : 'Download for'}</span>
        <span className="block text-sm">{configured ? label : '暂未配置'}</span>
      </div>
    </a>
  );
}

/* ── Product Card ── */
function ProductCard({
  icon, name, tagline, features, metrics, imageUrl, index, downloads,
}: {
  icon: string; name: string; tagline: string; features: string[]; metrics: { label: string; value: string }[];
  imageUrl: string; index: number; downloads: DownloadSettings;
}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.2 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`data-panel overflow-hidden group transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      {/* Product image */}
      <div className="relative h-56 md:h-64 overflow-hidden">
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/30 to-transparent"></div>
        {/* Floating badge */}
        <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-950/70 backdrop-blur-md border border-background-400/30">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
          <span className="text-[10px] mono-data text-emerald-400 font-semibold tracking-wider">AVAILABLE</span>
        </div>
        {/* Icon badge */}
        <div className="absolute bottom-4 left-4 w-12 h-12 rounded-xl bg-background-950/80 backdrop-blur-md border border-background-400/30 flex items-center justify-center">
          <i className={`${icon} text-xl text-accent-400`}></i>
        </div>
      </div>

      <div className="p-6 md:p-8">
        {/* Title area */}
        <div className="mb-5">
          <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-2">{name}</h3>
          <p className="text-sm text-foreground-400 font-light">{tagline}</p>
        </div>

        {/* Features */}
        <div className="space-y-3 mb-6">
          {features.map((f, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-md bg-accent-500/10 border border-accent-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-[10px] text-accent-400"></i>
              </div>
              <span className="text-sm text-foreground-300 leading-relaxed">{f}</span>
            </div>
          ))}
        </div>

        {/* Metrics row */}
        <div className="flex flex-wrap gap-3 mb-6 pt-4 border-t border-background-300/30">
          {metrics.map((m) => (
            <div key={m.label} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-200/50 border border-background-300/20">
              <span className="text-[10px] mono-data text-foreground-600">{m.label}</span>
              <span className="text-[11px] mono-data font-bold text-accent-400">{m.value}</span>
            </div>
          ))}
        </div>

        {/* Download buttons */}
        <div className="flex flex-wrap gap-3">
          <DownloadButton platform="ios" href={downloads.iosAppStoreUrl} label="App Store" />
          <DownloadButton platform="android" href={downloads.androidApkUrl} label="Android APK" />
        </div>
      </div>
    </div>
  );
}

/* ── Page ── */
const products = [
  {
    icon: 'ri-lock-star-line',
    name: '蚂蚁量化加密',
    tagline: '端到端量化策略安全通信与交易数据加密，军工级安全防护',
    features: [
      '端到端加密通信，保障量化策略传输安全',
      '硬件级密钥管理（HSM），防止策略泄露',
      '实时交易数据加密，支持多交易所API密钥托管',
      '零知识证明验证，策略回测数据全程加密',
    ],
    metrics: [
      { label: '加密强度', value: 'AES-256' },
      { label: '延迟', value: '<0.3ms' },
      { label: '支持交易所', value: '60+' },
    ],
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20dark%20futuristic%20cybersecurity%20visual%20with%20glowing%20encrypted%20data%20streams%20in%20green%20and%20gold%20on%20deep%20navy%20background%2C%20digital%20lock%20symbol%2C%20quantum%20encryption%20visualization%2C%20fintech%20security%20aesthetic%2C%20cinematic%20lighting%2C%204k%20detail&width=800&height=480&seq=download-encrypt-01&orientation=landscape',
  },
  {
    icon: 'ri-stock-line',
    name: '蚂蚁量化美股',
    tagline: 'AI驱动的美股量化策略终端，实时信号 + 智能组合优化',
    features: [
      '覆盖纳斯达克、纽交所全量美股实时行情与Level 2数据',
      '多因子Alpha模型，日频信号推送与自动预警',
      '智能执行算法（VWAP/TWAP），最小化交易成本',
      '组合优化引擎，支持风险平价与Black-Litterman模型',
    ],
    metrics: [
      { label: '覆盖标的', value: '8,200+' },
      { label: '信号频率', value: '日频' },
      { label: '夏普比率', value: '2.4' },
    ],
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20dark%20financial%20technology%20visual%20with%20glowing%20stock%20market%20data%20candlestick%20charts%20in%20emerald%20green%20and%20gold%20on%20deep%20navy%20background%2C%20algorithmic%20trading%20visualization%2C%20bull%20market%20energy%2C%20fintech%20dashboard%20aesthetic%2C%20cinematic%20lighting%2C%204k%20detail&width=800&height=480&seq=download-usstock-01&orientation=landscape',
  },
  {
    icon: 'ri-exchange-dollar-line',
    name: '蚂蚁量化外汇',
    tagline: '全球外汇市场量化分析工具，覆盖主要货币对与交叉盘',
    features: [
      '覆盖G10及新兴市场30+货币对实时报价与深度数据',
      'CTA趋势跟踪 + 波动率曲面套利策略引擎',
      '宏观经济事件驱动分析，央行政策实时解析',
      '跨境汇率风险管理，支持远期、期权组合对冲',
    ],
    metrics: [
      { label: '覆盖货币对', value: '30+' },
      { label: '延迟', value: '<5ms' },
      { label: '胜率', value: '58%' },
    ],
    imageUrl: 'https://readdy.ai/api/search-image?query=Abstract%20dark%20forex%20trading%20visual%20with%20glowing%20currency%20pairs%20and%20exchange%20rate%20charts%20in%20gold%20and%20emerald%20on%20deep%20navy%20background%2C%20global%20currency%20network%20visualization%2C%20quantitative%20trading%20aesthetic%2C%20fintech%20dashboard%2C%20cinematic%20lighting%2C%204k%20detail&width=800&height=480&seq=download-forex-01&orientation=landscape',
  },
];

export default function Downloads() {
  const [downloads, setDownloads] = useState<DownloadSettings>({
    iosAppStoreUrl: '',
    androidApkUrl: '',
    androidApkPath: '',
    androidApkName: '',
    androidApkSize: 0,
    wechatQrUrl: '',
    wechatQrPath: '',
    updatedAt: '',
  });

  useEffect(() => {
    getDownloadSettings()
      .then((result) => setDownloads(result.item))
      .catch(() => undefined);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {/* ── Hero ── */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-50 overflow-hidden">
        <div className="absolute inset-0 bg-dot-matrix opacity-25"></div>
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
          {[...Array(6)].map((_, i) => (
            <line key={i} x1={`${10 + i * 16}%`} y1="0" x2={`${30 + i * 12}%`} y2="100%" stroke="rgba(255,255,255,0.6)" strokeWidth="0.5" />
          ))}
        </svg>
        {/* Glow orbs */}
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-500/4 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-accent-500/4 rounded-full blur-[100px]"></div>

        <div className="relative px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <p className="text-accent-500 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase mb-4">Product Downloads</p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-100 leading-tight">
              量化产品中心
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-400 max-w-2xl leading-relaxed font-light">
              AI驱动的量化策略工具套件，覆盖加密通信、美股研究与外汇分析三大领域。支持 iOS App Store 与 Android APK 下载，为专业投资者提供一站式量化终端体验。
            </p>

            {/* Live stats bar */}
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-300/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                <span className="text-xs mono-data text-foreground-500">累计下载</span>
                <span className="text-sm mono-data font-bold text-foreground-100">128,600+</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-300/30">
                <i className="ri-star-fill text-xs text-accent-400"></i>
                <span className="text-xs mono-data text-foreground-500">App Store 评分</span>
                <span className="text-sm mono-data font-bold text-accent-400">4.8</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-300/30">
                <i className="ri-shield-check-line text-xs text-emerald-400"></i>
                <span className="text-xs mono-data text-foreground-500">安全认证</span>
                <span className="text-sm mono-data font-bold text-emerald-400">ISO 27001</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Product Cards ── */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">Available Products</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">选择您的量化工具</h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto font-light">
                三款产品覆盖加密安全、美股量化与外汇分析，支持 iOS 与 Android 双平台
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {products.map((p, i) => (
                <ProductCard key={p.name} {...p} index={i} downloads={downloads} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── System Requirements ── */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs md:text-sm font-semibold tracking-[0.25em] uppercase text-accent-500 mb-3">System Requirements</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100">系统要求</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-7 max-w-4xl mx-auto">
              {/* iOS */}
              <div className="data-panel p-6 md:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-foreground-100/5 border border-foreground-200/20 flex items-center justify-center">
                    <i className="ri-apple-fill text-xl text-foreground-100"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-bold text-foreground-100">iOS</h3>
                    <p className="text-xs text-foreground-500">App Store</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    '系统版本：iOS 16.0 或更高',
                    '兼容设备：iPhone / iPad',
                    '需要网络连接以获取实时行情数据',
                    '建议开启生物识别（Face ID / Touch ID）保护交易安全',
                  ].map((r, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="w-1 h-1 rounded-full bg-accent-400 mt-2 flex-shrink-0"></span>
                      <span className="text-sm text-foreground-400">{r}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Android */}
              <div className="data-panel p-6 md:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <i className="ri-android-fill text-xl text-emerald-400"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-bold text-foreground-100">Android</h3>
                    <p className="text-xs text-foreground-500">Google Play / APK 直装</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    '系统版本：Android 12.0 或更高',
                    '兼容设备：智能手机 / 平板',
                    '需要网络连接以获取实时行情数据',
                    'APK 安装需开启「允许安装未知来源应用」',
                  ].map((r, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <span className="w-1 h-1 rounded-full bg-emerald-400 mt-2 flex-shrink-0"></span>
                      <span className="text-sm text-foreground-400">{r}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 md:py-20 bg-background-100 border-t border-background-300/20">
        <div className="px-6 md:px-12 lg:px-20 text-center">
          <div className="max-w-[600px] mx-auto">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-4">
              需要定制化部署？
            </h3>
            <p className="text-sm text-foreground-400 mb-7 font-light leading-relaxed">
              我们提供面向机构客户的私有化部署方案，支持内网环境、定制策略模块与专属API对接。请联系我们的技术团队获取详细方案。
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full text-sm font-semibold whitespace-nowrap bg-accent-500 text-background-950 hover:bg-accent-400 transition-all duration-300 shadow-[0_0_30px_rgba(200,160,80,0.15)] hover:shadow-[0_0_40px_rgba(200,160,80,0.25)]"
            >
              联系技术团队
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
